'use client';
import { Popover, Transition } from '@headlessui/react';
import {
    ArrowTopRightOnSquareIcon,
    ExclamationCircleIcon,
} from '@heroicons/react/20/solid';
import { Fragment, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useEditor } from '../context/EditorContext/EditorContext';
import { useNotifications } from '../context/NotificationContext/NotificationContext';
import { collectUsedImages } from '../helpers/altTextAudit';
import {
    LARGE_IMAGE_SIZE_LIMIT,
    readImageSizeBytes,
} from '../helpers/imageSize';
import { getPublishDestinationUrl } from '../helpers/publishDestination';
import { API } from '../utils/api';
import { AltTextEditor } from './AltTextEditor';
import { ConfirmationModal } from '../ConfirmationModal';

const PUBLISH_POLL_INTERVAL_MS = 2500;
const PUBLISH_TIMEOUT_MS = 10 * 60 * 1000;
const DNS_HEALTH_CONFIRMATION_DELAY_MS = 5000;
const FAILED_DEPLOYMENT_STATES = new Set(['ERROR', 'CANCELED', 'CANCELLED']);
const PUBLISH_PROGRESS_STAGES = [
    {
        label: 'Creating deployment',
        detail: 'Committing site changes to GitHub and requesting a Vercel deployment.',
    },
    {
        label: 'Building and deploying',
        detail: 'Deploying on Vercel and waiting until the deployment is ready.',
    },
    {
        label: 'Verifying publication',
        detail: 'Refreshing domain, DNS, and deployment health.',
    },
];

const wait = (duration) =>
    new Promise((resolve) => window.setTimeout(resolve, duration));

async function waitForPublishDeployment(companyId, deploymentId, isCurrent) {
    const startedAt = Date.now();
    let consecutivePollFailures = 0;

    while (Date.now() - startedAt < PUBLISH_TIMEOUT_MS) {
        await wait(PUBLISH_POLL_INTERVAL_MS);
        if (!isCurrent()) return null;

        let deployment;
        try {
            deployment = await API.get(
                `${companyId}/publish/${encodeURIComponent(deploymentId)}`
            );
            consecutivePollFailures = 0;
        } catch (error) {
            consecutivePollFailures += 1;
            if (consecutivePollFailures >= 3) throw error;
            continue;
        }
        const status = String(deployment?.status || '').toUpperCase();

        if (status === 'READY') return deployment;
        if (FAILED_DEPLOYMENT_STATES.has(status)) {
            throw new Error(`Vercel deployment ${status.toLowerCase()}`);
        }
    }

    throw new Error('Publishing timed out while waiting for Vercel');
}

function PublishOutsideClick({ open, close, buttonRef, panelRef }) {
    useEffect(() => {
        if (!open) return;

        const ownerDoc = buttonRef.current?.ownerDocument;
        if (!ownerDoc) return;

        const isInsideRef = (ref, target) => {
            const el = ref.current;
            if (!el || !target) return false;
            try {
                return el === target || el.contains(target);
            } catch {
                return false;
            }
        };

        const handlePointerDown = (event) => {
            const target = event.target;
            if (!target || typeof target !== 'object') return;

            if (isInsideRef(buttonRef, target)) return;
            if (isInsideRef(panelRef, target)) return;

            close();
        };

        const docs = new Set([ownerDoc]);
        ownerDoc.querySelectorAll('iframe').forEach((frame) => {
            try {
                if (frame.contentDocument) docs.add(frame.contentDocument);
            } catch {
                // Ignore cross-origin frames
            }
        });

        docs.forEach((doc) => {
            doc.addEventListener('pointerdown', handlePointerDown, true);
        });

        return () => {
            docs.forEach((doc) => {
                doc.removeEventListener('pointerdown', handlePointerDown, true);
            });
        };
    }, [open, close, buttonRef, panelRef]);

    return null;
}

function extractHostname(value) {
    const raw = String(value || '').trim();
    if (!raw) return '';

    try {
        if (/^https?:\/\//i.test(raw)) {
            return new URL(raw).hostname;
        }
        return new URL(`https://${raw}`).hostname;
    } catch {
        return raw.replace(/^https?:\/\//i, '').split('/')[0];
    }
}

function normalizeSiteUrl(value) {
    const raw = String(value || '').trim();
    if (!raw) return '';

    return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
}

function slugifyPreview(value) {
    return String(value || '')
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 63);
}

function formatPublishedDate(value, now = Date.now()) {
    if (!value) return null;
    const publishedAt = new Date(value).getTime();
    if (!Number.isFinite(publishedAt)) return null;

    const elapsedHours = Math.max(0, Math.floor((now - publishedAt) / 3600000));
    if (elapsedHours < 1) return 'Published just now';
    if (elapsedHours < 24) return `Published ${elapsedHours}h ago`;

    const elapsedDays = Math.floor(elapsedHours / 24);
    const date = new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    }).format(publishedAt);
    return `Published ${elapsedDays} day${
        elapsedDays === 1 ? '' : 's'
    } ago on ${date}`;
}

const PUBLICATION_STATUS_META = {
    checking: {
        label: 'Checking…',
        className: 'bg-neutral-700 text-neutral-300',
    },
    published: {
        label: 'Published',
        className: 'bg-emerald-900/70 text-emerald-300',
    },
    not_published: {
        label: 'Not published',
        className: 'bg-neutral-700 text-neutral-300',
    },
    dns_issue: {
        label: 'DNS issue',
        className: 'bg-amber-900/70 text-amber-300',
    },
    deploying: {
        label: 'Deploying',
        className: 'bg-blue-900/70 text-blue-300',
    },
    deployment_failed: {
        label: 'Deployment failed',
        className: 'bg-red-900/70 text-red-300',
    },
};

function PublicationStatusTag({ status }) {
    const meta =
        PUBLICATION_STATUS_META[status] || PUBLICATION_STATUS_META.checking;
    return (
        <span
            className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-semibold ${meta.className}`}
        >
            {meta.label}
        </span>
    );
}

function PublishRocketIcon({ className = '' }) {
    const flameGradientId = `publish-flame-${useId().replace(/:/g, '')}`;

    return (
        <svg viewBox="0 0 640 640" className={className} aria-hidden="true">
            <defs>
                <linearGradient
                    id={flameGradientId}
                    x1="100%"
                    y1="0%"
                    x2="0%"
                    y2="100%"
                >
                    <stop
                        offset="0%"
                        stopColor="var(--publish-flame-start, currentColor)"
                    />
                    <stop
                        offset="100%"
                        stopColor="var(--publish-flame-end, currentColor)"
                    />
                </linearGradient>
            </defs>
            <path
                fill="currentColor"
                d="M121 352H224.5C259.8 352 288.5 380.7 288.5 416V519.5C288.5 544.4 315.6 559.7 337 546.9L425.2 494C439.7 485.3 448.5 469.7 448.5 452.8V357.8C577.4 281.8 583.9 168.3 572.8 92.4C570.9 79.6 560.9 69.6 548.1 67.7C472.2 56.6 358.7 63.1 282.6 192H187.6C170.7 192 155.1 200.8 146.4 215.3L93.6 303.5C80.8 324.9 96.1 352 121 352ZM448.5 144C475 144 496.5 165.5 496.5 192C496.5 218.5 475 240 448.5 240C422 240 400.5 218.5 400.5 192C400.5 165.5 422 144 448.5 144Z"
            />
            <path
                className="publish-rocket-flame"
                fill={`url(#${flameGradientId})`}
                d="M216.9 537.6C248.4 506.1 248.4 455.1 216.9 423.6C185.4 392.1 134.4 392.1 102.9 423.6C71.6 454.9 65.4 515.6 64.6 550C64.2 564.6 75.8 576.2 90.5 575.9C125 575.1 185.6 568.9 216.9 537.6ZM176.3 505.6C166.2 515.7 147.8 518.6 135 519.3C127 519.8 120.7 513.4 121.1 505.4C121.8 492.6 124.8 474.2 134.8 464.1C146.2 452.7 164.8 452.7 176.2 464.1C187.6 475.5 187.6 494.1 176.2 505.5Z"
            />
        </svg>
    );
}

function PublishProgress({ currentStage, complete, onClose }) {
    return (
        <div className="publish-progress-panel -m-3 overflow-hidden rounded-md">
            <div className="border-b border-neutral-600 px-4 py-3">
                <h3 className="text-[12px] font-semibold text-white">
                    {complete
                        ? 'Publishing complete'
                        : 'Publishing in progress…'}
                </h3>
            </div>
            <ol className="space-y-3 p-4">
                {PUBLISH_PROGRESS_STAGES.map((stage, index) => {
                    const done = complete || index < currentStage;
                    const active = !complete && index === currentStage;
                    return (
                        <li
                            key={stage.label}
                            className={`flex items-start gap-2.5 text-[12px] ${
                                done || active
                                    ? 'text-neutral-100'
                                    : 'text-neutral-500'
                            }`}
                            aria-current={active ? 'step' : undefined}
                        >
                            {done ? (
                                <svg
                                    className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    aria-hidden="true"
                                >
                                    <circle
                                        cx="10"
                                        cy="10"
                                        r="8"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                    />
                                    <path
                                        d="m6.5 10 2.2 2.2 4.8-5"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            ) : active ? (
                                <svg
                                    className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-emerald-400"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    aria-hidden="true"
                                >
                                    <circle
                                        className="opacity-30"
                                        cx="10"
                                        cy="10"
                                        r="8"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                    />
                                    <path
                                        d="M10 2a8 8 0 0 1 8 8"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            ) : (
                                <span className="mt-0.5 h-4 w-4 shrink-0 rounded-full border border-neutral-600" />
                            )}
                            <span className="min-w-0">
                                <span className="block font-medium">
                                    {stage.label}
                                </span>
                                <span
                                    className={`leading-3.5 mt-0.5 block text-[10px] ${
                                        done || active
                                            ? 'text-neutral-400'
                                            : 'text-neutral-600'
                                    }`}
                                >
                                    {stage.detail}
                                </span>
                            </span>
                        </li>
                    );
                })}
            </ol>
            <div className="flex justify-end border-t border-neutral-600 px-4 py-3">
                <button
                    type="button"
                    onClick={onClose}
                    className="rounded-md border border-neutral-500 bg-neutral-700 px-3 py-1.5 text-[12px] font-medium text-white shadow-sm transition-colors hover:bg-neutral-600 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                >
                    Close
                </button>
            </div>
        </div>
    );
}

export function Publish({ buttonClass = '' } = {}) {
    const editor = useEditor();
    const notifications = useNotifications();
    const { company_id, company_name, domain, slug } = editor.global.data;
    const [loading, setLoading] = useState(false);
    const [publishPhase, setPublishPhase] = useState('idle');
    const [publishProgressStage, setPublishProgressStage] = useState(0);
    const [publicationStatusKnown, setPublicationStatusKnown] = useState(false);
    const [canUnpublish, setCanUnpublish] = useState(false);
    const [unpublishOpen, setUnpublishOpen] = useState(false);
    const [unpublishing, setUnpublishing] = useState(false);
    const [webflowPublishConfirmOpen, setWebflowPublishConfirmOpen] =
        useState(false);
    const [publishDestinations, setPublishDestinations] = useState({
        production: true,
        staging: false,
    });
    const [publishedDestinations, setPublishedDestinations] = useState({
        production: false,
        staging: false,
    });
    const [publishedAt, setPublishedAt] = useState({
        production: null,
        staging: null,
    });
    const [now, setNow] = useState(() => Date.now());
    const [destinationHealth, setDestinationHealth] = useState({
        production: 'checking',
        staging: 'checking',
    });
    const [productionWarning, setProductionWarning] = useState(null);
    const [productionHostingProvider, setProductionHostingProvider] =
        useState(null);
    const [resolvedLiveDomain, setResolvedLiveDomain] = useState('');
    const [mounted, setMounted] = useState(false);
    const [altTextPanelOpen, setAltTextPanelOpen] = useState(false);
    const [altTextPanelFocus, setAltTextPanelFocus] = useState('alt-text');
    const [altTextSources, setAltTextSources] = useState([]);
    const [imageSizesByUrl, setImageSizesByUrl] = useState({});
    const [hasCustomFavicon, setHasCustomFavicon] = useState(null);
    const buttonRef = useRef(null);
    const panelRef = useRef(null);
    const publishAttemptRef = useRef(0);
    const [panelPos, setPanelPos] = useState({ top: 0, left: 0 });
    const liveDomain = resolvedLiveDomain || extractHostname(domain);
    const liveUrl = normalizeSiteUrl(liveDomain);
    const previewSlug =
        slugifyPreview(slug) || slugifyPreview(company_name) || company_id;
    const previewDomain = `${previewSlug}.nicejob.site`;
    const apexLiveDomain = liveDomain.replace(/^www\./i, '');
    const currentPageName = editor.page.name || 'index';
    const previewPageUrl = getPublishDestinationUrl(
        previewDomain,
        currentPageName
    );
    const livePageUrl = getPublishDestinationUrl(liveUrl, currentPageName);

    const applyPublicationStatus = (status) => {
        const statusDomains = Array.isArray(status.production?.domains)
            ? status.production.domains
            : Array.isArray(status.domains)
            ? status.domains
            : [];
        const statusLiveDomain =
            statusDomains
                .map(extractHostname)
                .find((candidate) => candidate.startsWith('www.')) ||
            statusDomains.map(extractHostname).find(Boolean);
        if (statusLiveDomain) setResolvedLiveDomain(statusLiveDomain);

        const nextPublished = {
            production:
                status.production?.published === true ||
                (status.production === undefined && status.published === true),
            staging: status.staging?.published === true,
        };
        setPublishedDestinations(nextPublished);
        setPublishedAt({
            production: status.production?.publishedAt || null,
            staging: status.staging?.publishedAt || null,
        });
        setPublishDestinations(
            nextPublished.production || nextPublished.staging
                ? nextPublished
                : liveDomain
                ? { production: true, staging: false }
                : { production: false, staging: true }
        );
        setDestinationHealth({
            production:
                status.production?.status ||
                (nextPublished.production ? 'published' : 'not_published'),
            staging:
                status.staging?.status ||
                (nextPublished.staging ? 'published' : 'not_published'),
        });
        setProductionWarning(status.production?.warning || null);
        setProductionHostingProvider(
            status.production?.hostingProvider || null
        );
        setPublishPhase(status.published ? 'published' : 'unpublished');
        setCanUnpublish(status.canUnpublish === true);
        setPublicationStatusKnown(true);
    };
    const effectiveAltTextSources = useMemo(() => {
        const loadedSources = altTextSources.length
            ? altTextSources
            : editor.components.data.map((component) => ({
                  id: `${currentPageName}:${component.data_file}`,
                  pageName: currentPageName,
                  dataFile: component.data_file,
                  componentName: component.name,
                  props: component.props,
                  global: ['nav.json', 'footer.json'].includes(
                      component.data_file
                  ),
              }));

        return loadedSources.map((source) => {
            const currentComponent = editor.components.data.find(
                (component) => component.data_file === source.dataFile
            );
            const belongsToCurrentEditor =
                source.global || source.pageName === currentPageName;
            if (!currentComponent || !belongsToCurrentEditor) return source;

            const props = structuredClone(currentComponent.props);
            delete props.company;
            delete props.brand;
            return { ...source, props };
        });
    }, [altTextSources, currentPageName, editor.components.data]);
    const usedImages = useMemo(
        () =>
            collectUsedImages(
                effectiveAltTextSources.map((source) => ({
                    sourceId: source.id,
                    pageName: source.pageName,
                    data_file: source.dataFile,
                    name: source.componentName,
                    props: source.props,
                }))
            ),
        [effectiveAltTextSources]
    );
    const missingAltTextCount = usedImages.filter(
        (image) => image.missingAltText
    ).length;
    const hasMissingAltText = missingAltTextCount > 0;
    const usedImageUrlsKey = usedImages.map((image) => image.url).join('\n');
    const heavyImageCount = usedImages.filter(
        (image) =>
            Number(imageSizesByUrl[image.url] || 0) > LARGE_IMAGE_SIZE_LIMIT
    ).length;
    const hasHeavyImages = heavyImageCount > 0;
    const hasMissingFavicon = hasCustomFavicon === false;
    const hasPublishWarnings =
        hasMissingAltText || hasHeavyImages || hasMissingFavicon;
    const publishButtonClass = hasPublishWarnings
        ? `${
              buttonClass ||
              'rounded-full p-1.5 text-white transition-all focus:outline-none'
          } !bg-amber-400 !text-neutral-900 hover:!bg-amber-500`
        : `${
              buttonClass ||
              'rounded-full p-1.5 text-white transition-all focus:outline-none'
          } !bg-[#8F52F1] !text-white hover:!bg-[#7C3AED]`;

    useEffect(() => setMounted(true), []);

    useEffect(() => {
        const timer = window.setInterval(() => setNow(Date.now()), 60000);
        return () => window.clearInterval(timer);
    }, []);

    useEffect(() => {
        if (publishPhase !== 'success') return undefined;
        const timer = window.setTimeout(
            () => setPublishPhase('published'),
            1550
        );
        return () => window.clearTimeout(timer);
    }, [publishPhase]);

    useEffect(
        () => () => {
            publishAttemptRef.current += 1;
        },
        []
    );

    useEffect(() => {
        let cancelled = false;
        const attempt = publishAttemptRef.current;

        const loadStatus = async () => {
            try {
                let status = await API.get(`${company_id}/publication-status`);
                if (status?.production?.status === 'dns_issue') {
                    await wait(DNS_HEALTH_CONFIRMATION_DELAY_MS);
                    if (cancelled || publishAttemptRef.current !== attempt)
                        return;
                    status = await API.get(`${company_id}/publication-status`);
                }

                if (
                    !cancelled &&
                    publishAttemptRef.current === attempt &&
                    status
                ) {
                    applyPublicationStatus(status);
                }
            } catch (error) {
                console.warn(
                    '[Publish] Unable to load publication status.',
                    error
                );
            }
        };

        loadStatus();

        return () => {
            cancelled = true;
        };
    }, [company_id]);

    useEffect(() => {
        const handleOpenAltText = (event) => {
            const focus =
                event.detail?.focus === 'heavy' ? 'heavy' : 'alt-text';
            setAltTextPanelFocus(focus);
            setAltTextPanelOpen(true);
        };

        window.addEventListener('nicejob:open-alt-text', handleOpenAltText);
        return () =>
            window.removeEventListener(
                'nicejob:open-alt-text',
                handleOpenAltText
            );
    }, []);

    useEffect(() => {
        let cancelled = false;
        const loadFaviconStatus = () => {
            fetch('/api/favicon-status')
                .then((response) => {
                    if (!response.ok)
                        throw new Error('Unable to audit favicon');
                    return response.json();
                })
                .then((status) => {
                    if (!cancelled) {
                        setHasCustomFavicon(status?.hasCustomFavicon === true);
                    }
                })
                .catch((error) => {
                    console.warn('[Publish] Favicon audit failed.', error);
                });
        };
        const handleFaviconUpdated = () => setHasCustomFavicon(true);

        loadFaviconStatus();
        window.addEventListener('editor:favicon-updated', handleFaviconUpdated);
        return () => {
            cancelled = true;
            window.removeEventListener(
                'editor:favicon-updated',
                handleFaviconUpdated
            );
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        const libraryImages = Array.isArray(editor.site.data?.images)
            ? editor.site.data.images
            : [];

        Promise.all(
            usedImages.map(async (image) => {
                const libraryImage = libraryImages.find(
                    (candidate) =>
                        String(candidate?.url || '').trim() === image.url
                );
                const librarySize = readImageSizeBytes(libraryImage);
                if (librarySize) return [image.url, librarySize];

                try {
                    const response = await fetch(
                        `/api/image-metadata?url=${encodeURIComponent(
                            image.url
                        )}`
                    );
                    if (!response.ok) return [image.url, 0];
                    const metadata = await response.json();
                    return [image.url, Number(metadata?.sizeBytes || 0)];
                } catch {
                    return [image.url, 0];
                }
            })
        ).then((entries) => {
            if (!cancelled) setImageSizesByUrl(Object.fromEntries(entries));
        });

        return () => {
            cancelled = true;
        };
    }, [editor.site.data?.images, usedImageUrlsKey]);

    useEffect(() => {
        let cancelled = false;
        fetch('/api/editor-alt-text-sources')
            .then((response) => {
                if (!response.ok)
                    throw new Error('Unable to audit site images');
                return response.json();
            })
            .then((payload) => {
                if (!cancelled && Array.isArray(payload?.sources)) {
                    setAltTextSources(payload.sources);
                }
            })
            .catch((error) => {
                console.warn(
                    '[Publish] Site-wide alt text audit failed.',
                    error
                );
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const performPublish = async ({
        confirmWebflowReplacement = false,
    } = {}) => {
        const environments = ['production', 'staging'].filter(
            (environment) => publishDestinations[environment]
        );
        if (!environments.length) {
            notifications.push({
                title: 'Select a publish destination',
                variant: 'error',
            });
            return;
        }
        const attempt = publishAttemptRef.current + 1;
        publishAttemptRef.current = attempt;
        setLoading(true);
        setPublishPhase('publishing');
        setPublishProgressStage(0);
        setDestinationHealth((current) => ({
            ...current,
            ...Object.fromEntries(
                environments.map((environment) => [environment, 'deploying'])
            ),
        }));
        try {
            for (const environment of environments) {
                const result = await API.post(`${company_id}/publish`, {
                    env: environment,
                    ...(environment === 'production'
                        ? { confirmWebflowReplacement }
                        : {}),
                });
                if (!result?.deploymentId) {
                    throw new Error('Publish did not return a deployment ID');
                }
                if (environment === 'production' && result.canonicalUrl) {
                    editor.global.sync({
                        key: 'canonical_url',
                        value: result.canonicalUrl,
                    });
                }

                setPublishProgressStage(1);
                await waitForPublishDeployment(
                    company_id,
                    result.deploymentId,
                    () => publishAttemptRef.current === attempt
                );
            }
            if (publishAttemptRef.current !== attempt) return;

            setPublishedDestinations((current) => ({
                ...current,
                ...Object.fromEntries(
                    environments.map((environment) => [environment, true])
                ),
            }));
            setDestinationHealth((current) => ({
                ...current,
                ...Object.fromEntries(
                    environments.map((environment) => [
                        environment,
                        'published',
                    ])
                ),
            }));
            if (publishDestinations.production) {
                setCanUnpublish(true);
                setPublicationStatusKnown(true);
            }

            setPublishProgressStage(2);
            try {
                let refreshedStatus = await API.get(
                    `${company_id}/publication-status`
                );
                if (refreshedStatus?.production?.status === 'dns_issue') {
                    await wait(DNS_HEALTH_CONFIRMATION_DELAY_MS);
                    if (publishAttemptRef.current !== attempt) return;
                    refreshedStatus = await API.get(
                        `${company_id}/publication-status`
                    );
                }
                if (refreshedStatus) applyPublicationStatus(refreshedStatus);
            } catch (statusError) {
                console.warn(
                    '[Publish] Unable to refresh publication health.',
                    statusError
                );
            }
            setPublishPhase('success');
            notifications.push({
                title: 'Publishing complete!',
                message:
                    environments.length > 1
                        ? 'Live site and preview site published.'
                        : environments[0] === 'staging'
                        ? 'Preview site published.'
                        : 'Live site published.',
                variant: 'success',
            });
        } catch (error) {
            if (publishAttemptRef.current !== attempt) return;
            setPublishPhase('idle');
            if (error?.code === 'WEBFLOW_PUBLISH_CONFIRMATION_REQUIRED') {
                setProductionHostingProvider('webflow');
                setProductionWarning(
                    'This site is currently published on Webflow.'
                );
                setWebflowPublishConfirmOpen(true);
                return;
            }
            const isDnsLimit = error?.code === 'CLOUDFLARE_DNS_RECORD_LIMIT';
            notifications.push({
                title: isDnsLimit
                    ? 'Cloudflare DNS limit reached'
                    : 'Failed to publish',
                message: isDnsLimit
                    ? 'Remove unused preview-site records in Cloudflare, then try again.'
                    : undefined,
                variant: 'error',
                error,
            });
        } finally {
            setLoading(false);
        }
    };

    const handlePublish = () => {
        if (publishDestinations.production && !liveDomain) {
            notifications.push({
                title: 'Add a domain in NiceJob Setup',
                message:
                    'A domain is required before publishing the live site.',
                variant: 'error',
            });
            return;
        }

        if (
            publishDestinations.production &&
            productionHostingProvider === 'webflow'
        ) {
            setWebflowPublishConfirmOpen(true);
            return;
        }

        performPublish();
    };

    const handleUnpublish = async () => {
        const environments = ['production', 'staging'].filter(
            (environment) =>
                publishDestinations[environment] &&
                publishedDestinations[environment]
        );
        if (!environments.length) return;
        setUnpublishing(true);
        try {
            await API.delete(`${company_id}/publish`, { environments });
            const nextPublished = {
                ...publishedDestinations,
                ...Object.fromEntries(
                    environments.map((environment) => [environment, false])
                ),
            };
            setPublishedDestinations(nextPublished);
            setDestinationHealth((current) => ({
                ...current,
                ...Object.fromEntries(
                    environments.map((environment) => [
                        environment,
                        'not_published',
                    ])
                ),
            }));
            if (environments.includes('production')) {
                setProductionWarning(null);
            }
            setPublishDestinations((current) => ({
                ...current,
                ...Object.fromEntries(
                    environments.map((environment) => [environment, false])
                ),
            }));
            setPublishPhase(
                nextPublished.production || nextPublished.staging
                    ? 'published'
                    : 'unpublished'
            );
            setCanUnpublish(nextPublished.production || nextPublished.staging);
            setPublicationStatusKnown(true);
            setUnpublishOpen(false);
            notifications.push({
                title:
                    environments.length > 1
                        ? 'Sites unpublished'
                        : environments[0] === 'staging'
                        ? 'Preview site unpublished'
                        : 'Live site unpublished',
                variant: 'success',
            });
        } catch (error) {
            notifications.push({
                title: 'Failed to unpublish',
                variant: 'error',
                error,
            });
        } finally {
            setUnpublishing(false);
        }
    };

    const updatePanelPos = () => {
        if (!buttonRef.current) return;
        const rect = buttonRef.current.getBoundingClientRect();
        const panelWidth = 384; // matches w-96
        const left = Math.min(
            Math.max(16, rect.right - panelWidth),
            typeof window !== 'undefined'
                ? window.innerWidth - panelWidth - 16
                : rect.right - panelWidth
        );
        const top = rect.bottom + 16;
        setPanelPos({ top, left });
    };

    useEffect(() => {
        const onResize = () => updatePanelPos();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    useEffect(() => {
        if (liveDomain) return;
        setPublishDestinations((current) =>
            !current.production && current.staging
                ? current
                : { production: false, staging: true }
        );
    }, [liveDomain]);

    const canUnpublishSelected =
        (publishDestinations.production && publishedDestinations.production) ||
        (publishDestinations.staging && publishedDestinations.staging);
    const selectedPublishedDestinations = [
        publishDestinations.production && publishedDestinations.production
            ? 'production'
            : null,
        publishDestinations.staging && publishedDestinations.staging
            ? 'staging'
            : null,
    ].filter(Boolean);
    const showPublishProgress = loading || publishPhase === 'success';

    return (
        <>
            <Popover className="relative">
                {({ open, close }) => {
                    const portalTarget = buttonRef.current?.ownerDocument?.body;
                    return (
                        <>
                            <PublishOutsideClick
                                open={open}
                                close={close}
                                buttonRef={buttonRef}
                                panelRef={panelRef}
                            />

                            {/* 🚀 Rocket Button */}
                            <Popover.Button
                                ref={buttonRef}
                                onClick={() => {
                                    if (!open) updatePanelPos();
                                }}
                                onFocus={() => {
                                    if (!open) updatePanelPos();
                                }}
                                className={`${publishButtonClass} overflow-hidden`}
                                title={
                                    loading
                                        ? 'Publishing to the live site'
                                        : publishPhase === 'success'
                                        ? 'Published'
                                        : publishPhase === 'published'
                                        ? 'Published'
                                        : hasPublishWarnings
                                        ? 'SEO recommendations need attention'
                                        : 'Publish'
                                }
                                aria-label={
                                    loading ? 'Publishing site' : 'Publish site'
                                }
                            >
                                <span
                                    className="block h-4 w-4"
                                    aria-hidden="true"
                                >
                                    <span
                                        className={`block h-4 w-4 ${
                                            hasPublishWarnings
                                                ? 'publish-rocket-on-warning '
                                                : ''
                                        }${
                                            publishPhase === 'publishing'
                                                ? 'publish-rocket-publishing'
                                                : publishPhase === 'success'
                                                ? 'publish-rocket-success'
                                                : ''
                                        }`}
                                    >
                                        <PublishRocketIcon className="h-4 w-4 overflow-visible" />
                                    </span>
                                </span>
                            </Popover.Button>

                            <style>{`
                                .publish-rocket-publishing,
                                .publish-rocket-success {
                                    --publish-flame-start: #fb923c;
                                    --publish-flame-end: #dc2626;
                                }

                                .publish-rocket-flame {
                                    transform-box: fill-box;
                                    transform-origin: 83% 17%;
                                }

                                .publish-rocket-publishing
                                    .publish-rocket-flame {
                                    animation: publish-flame-burn 0.24s
                                        ease-in-out infinite alternate;
                                }

                                .publish-rocket-success {
                                    animation: publish-rocket-launch 1.55s
                                        ease-in-out both;
                                }

                                .publish-rocket-on-warning {
                                    color: #fffbeb;
                                    filter: drop-shadow(
                                        0 1px 1px rgba(120, 53, 15, 0.38)
                                    );
                                }

                                .publish-progress-visible
                                    .publish-panel-content {
                                    display: none;
                                }

                                @keyframes publish-flame-burn {
                                    from {
                                        opacity: 0.72;
                                        transform: translate(0.2px, 0.2px)
                                            scale(0.82);
                                    }
                                    to {
                                        opacity: 1;
                                        transform: translate(-0.5px, 0.6px)
                                            scale(1.12);
                                    }
                                }

                                @keyframes publish-rocket-launch {
                                    0% {
                                        opacity: 1;
                                        transform: translateY(0);
                                    }
                                    38% {
                                        opacity: 1;
                                        transform: translate(190%, -190%);
                                    }
                                    39% {
                                        opacity: 0;
                                        transform: translate(190%, -190%);
                                    }
                                    55% {
                                        opacity: 0;
                                        transform: translate(-190%, 190%);
                                    }
                                    56% {
                                        opacity: 1;
                                        transform: translate(-190%, 190%);
                                    }
                                    88%,
                                    100% {
                                        opacity: 1;
                                        transform: translateY(0);
                                    }
                                }

                                @media (prefers-reduced-motion: reduce) {
                                    .publish-rocket-publishing {
                                        animation: none;
                                    }

                                    .publish-rocket-publishing
                                        .publish-rocket-flame {
                                        animation: none;
                                    }

                                    .publish-rocket-success {
                                        animation: none;
                                        transform: none;
                                    }
                                }
                            `}</style>

                            {mounted &&
                                portalTarget &&
                                createPortal(
                                    <Transition
                                        show={open}
                                        as={Fragment}
                                        enter="transition ease-out duration-150"
                                        enterFrom="opacity-0 translate-y-1"
                                        enterTo="opacity-100 translate-y-0"
                                        leave="transition ease-in duration-100"
                                        leaveFrom="opacity-100 translate-y-0"
                                        leaveTo="opacity-0 translate-y-1"
                                    >
                                        <Popover.Panel
                                            ref={panelRef}
                                            static
                                            className={`z-[20000] w-96 max-w-[calc(100vw-2rem)] rounded-md bg-neutral-800 p-3 shadow-lg ring-1 ring-black/30 ${
                                                showPublishProgress
                                                    ? 'publish-progress-visible'
                                                    : ''
                                            }`}
                                            style={{
                                                position: 'fixed',
                                                top: panelPos.top,
                                                left: panelPos.left,
                                            }}
                                        >
                                            {showPublishProgress && (
                                                <PublishProgress
                                                    currentStage={
                                                        publishProgressStage
                                                    }
                                                    complete={
                                                        publishPhase ===
                                                        'success'
                                                    }
                                                    onClose={close}
                                                />
                                            )}
                                            <div className="publish-panel-content flex flex-col gap-1.5">
                                                {/* Staging preview */}
                                                <label
                                                    htmlFor="publish-staging"
                                                    className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-neutral-700"
                                                >
                                                    <input
                                                        id="publish-staging"
                                                        type="checkbox"
                                                        checked={
                                                            publishDestinations.staging
                                                        }
                                                        onChange={() =>
                                                            setPublishDestinations(
                                                                (current) => ({
                                                                    ...current,
                                                                    staging:
                                                                        !current.staging,
                                                                })
                                                            )
                                                        }
                                                        className="h-3.5 w-3.5 translate-y-3 accent-blue-600 focus:outline-none focus:ring-0 focus:ring-offset-0"
                                                    />
                                                    <div className="flex min-w-0 flex-1 flex-col leading-tight">
                                                        <span className="relative left-[-22px] mb-2 text-[12px] font-semibold text-white">
                                                            Staging Site
                                                        </span>
                                                        <span className="flex min-w-0 items-center gap-1.5 text-[12px] text-white">
                                                            <span className="break-all">
                                                                {previewDomain}
                                                            </span>
                                                            <PublicationStatusTag
                                                                status={
                                                                    destinationHealth.staging
                                                                }
                                                            />
                                                            <a
                                                                href={
                                                                    previewPageUrl
                                                                }
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                onClick={(
                                                                    event
                                                                ) =>
                                                                    event.stopPropagation()
                                                                }
                                                                className="inline-flex h-4 w-4 shrink-0 items-center justify-center text-neutral-300 transition-colors hover:text-[#2CE080] focus:outline-none"
                                                                title="Open preview site in a new tab"
                                                                aria-label={`Open ${previewDomain} in a new tab`}
                                                            >
                                                                <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                                                            </a>
                                                        </span>
                                                        {publishedDestinations.staging && (
                                                            <span className="mt-1 text-[10px] text-emerald-300">
                                                                {formatPublishedDate(
                                                                    publishedAt.staging,
                                                                    now
                                                                )}
                                                            </span>
                                                        )}
                                                    </div>
                                                </label>

                                                {/* Live */}
                                                <label
                                                    htmlFor="publish-live"
                                                    className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-neutral-700"
                                                >
                                                    <input
                                                        id="publish-live"
                                                        type="checkbox"
                                                        checked={
                                                            liveDomain &&
                                                            publishDestinations.production
                                                        }
                                                        disabled={!liveDomain}
                                                        onChange={() =>
                                                            setPublishDestinations(
                                                                (current) => ({
                                                                    ...current,
                                                                    production:
                                                                        !current.production,
                                                                })
                                                            )
                                                        }
                                                        className="h-3.5 w-3.5 translate-y-3 accent-green-600 focus:outline-none focus:ring-0 focus:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-40"
                                                    />
                                                    <div className="flex min-w-0 flex-1 flex-col leading-tight">
                                                        <span className="relative left-[-22px] mb-2 text-[12px] font-semibold text-white">
                                                            Live site
                                                        </span>
                                                        <span className="flex min-w-0 items-center gap-1.5 text-[12px] text-white">
                                                            <span className="break-all">
                                                                {liveDomain ||
                                                                    (publicationStatusKnown
                                                                        ? 'Domain not configured'
                                                                        : 'Checking domain…')}
                                                            </span>
                                                            <PublicationStatusTag
                                                                status={
                                                                    destinationHealth.production
                                                                }
                                                            />
                                                            {livePageUrl && (
                                                                <a
                                                                    href={
                                                                        livePageUrl
                                                                    }
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    onClick={(
                                                                        event
                                                                    ) =>
                                                                        event.stopPropagation()
                                                                    }
                                                                    className="inline-flex h-4 w-4 shrink-0 items-center justify-center text-neutral-300 transition-colors hover:text-[#2CE080] focus:outline-none"
                                                                    title="Open live site in a new tab"
                                                                    aria-label={`Open ${liveDomain} in a new tab`}
                                                                >
                                                                    <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                                                                </a>
                                                            )}
                                                        </span>
                                                        {publishedDestinations.production && (
                                                            <span className="mt-1 text-[10px] text-emerald-300">
                                                                {formatPublishedDate(
                                                                    publishedAt.production,
                                                                    now
                                                                )}
                                                            </span>
                                                        )}
                                                    </div>
                                                </label>
                                            </div>

                                            {!liveDomain && (
                                                <div
                                                    className="publish-panel-content mt-3 rounded-md border border-amber-500/40 bg-amber-950/40 px-2.5 py-2 text-[11px] leading-4 text-amber-200"
                                                    role="alert"
                                                >
                                                    <p className="flex items-center gap-1.5 font-semibold">
                                                        <ExclamationCircleIcon className="h-4 w-4 shrink-0" />
                                                        Domain required for the
                                                        live site
                                                    </p>
                                                    <p className="mt-0.5 text-amber-200/80">
                                                        Please add the domain in{' '}
                                                        <a
                                                            href="https://app.nicejob.com/setup/sites/domain-and-call-tracking"
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="font-semibold text-amber-100 underline underline-offset-2 hover:text-white"
                                                        >
                                                            NiceJob Setup
                                                        </a>
                                                        . You can publish the
                                                        staging site while the
                                                        client chooses a domain.
                                                    </p>
                                                </div>
                                            )}

                                            {productionHostingProvider ===
                                                'webflow' && (
                                                <div
                                                    className="publish-panel-content mt-3 rounded-md border border-amber-500/40 bg-amber-950/40 px-2.5 py-2 text-[11px] leading-4 text-amber-200"
                                                    role="alert"
                                                >
                                                    <p className="flex items-center gap-1.5 font-semibold">
                                                        <ExclamationCircleIcon className="h-4 w-4 shrink-0" />
                                                        Currently published on
                                                        Webflow
                                                    </p>
                                                    <p className="mt-0.5 text-amber-200/80">
                                                        This site is currently
                                                        published on Webflow.
                                                        Are you sure you want to
                                                        publish this version
                                                        instead?
                                                    </p>
                                                </div>
                                            )}

                                            {productionHostingProvider !==
                                                'webflow' &&
                                                destinationHealth.production ===
                                                    'dns_issue' && (
                                                    <div className="publish-panel-content mt-3 rounded-md border border-amber-500/40 bg-amber-950/40 px-2.5 py-2 text-[11px] leading-4 text-amber-200">
                                                        <p className="font-semibold">
                                                            Client domain is not
                                                            pointing to this
                                                            site
                                                        </p>
                                                        <p className="mt-0.5 text-amber-200/80">
                                                            {productionWarning ||
                                                                'The domain is connected in Vercel, but its DNS records point somewhere else. The client may have moved the website.'}
                                                        </p>
                                                    </div>
                                                )}

                                            {destinationHealth.production ===
                                                'deployment_failed' && (
                                                <div className="publish-panel-content mt-3 rounded-md border border-red-500/40 bg-red-950/40 px-2.5 py-2 text-[11px] leading-4 text-red-200">
                                                    <p className="font-semibold">
                                                        Production deployment
                                                        failed
                                                    </p>
                                                    <p className="mt-0.5 text-red-200/80">
                                                        Open the Vercel project
                                                        to review the latest
                                                        deployment logs.
                                                    </p>
                                                </div>
                                            )}

                                            {hasPublishWarnings && (
                                                <div className="publish-panel-content mt-3 border-t border-neutral-700 pt-3">
                                                    <h4 className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-amber-300">
                                                        <ExclamationCircleIcon className="h-4 w-4" />
                                                        SEO Recommendations
                                                    </h4>
                                                    {hasMissingAltText && (
                                                        <div>
                                                            <p className="mt-1.5 text-[12px] leading-4 text-neutral-200">
                                                                {
                                                                    missingAltTextCount
                                                                }{' '}
                                                                {missingAltTextCount ===
                                                                1
                                                                    ? 'image is'
                                                                    : 'images are'}{' '}
                                                                missing alt
                                                                text.
                                                            </p>
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    close();
                                                                    setAltTextPanelFocus(
                                                                        'alt-text'
                                                                    );
                                                                    setAltTextPanelOpen(
                                                                        true
                                                                    );
                                                                }}
                                                                className="mt-1.5 inline-flex items-center rounded bg-amber-400 px-2 py-1 text-[10px] font-semibold leading-none text-neutral-900 shadow-sm transition-colors hover:bg-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-300 focus:ring-offset-1 focus:ring-offset-neutral-800"
                                                            >
                                                                Fix missing alt
                                                                text
                                                            </button>
                                                        </div>
                                                    )}
                                                    {hasHeavyImages && (
                                                        <div
                                                            className={
                                                                hasMissingAltText
                                                                    ? 'mt-2.5 border-t border-neutral-700 pt-2.5'
                                                                    : ''
                                                            }
                                                        >
                                                            <p className="text-[12px] leading-4 text-neutral-200">
                                                                {
                                                                    heavyImageCount
                                                                }{' '}
                                                                {heavyImageCount ===
                                                                1
                                                                    ? 'image is'
                                                                    : 'images are'}{' '}
                                                                too heavy.
                                                            </p>
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    close();
                                                                    setAltTextPanelFocus(
                                                                        'heavy'
                                                                    );
                                                                    setAltTextPanelOpen(
                                                                        true
                                                                    );
                                                                }}
                                                                className="mt-1.5 inline-flex items-center rounded bg-red-500 px-2 py-1 text-[10px] font-semibold leading-none text-white shadow-sm transition-colors hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-1 focus:ring-offset-neutral-800"
                                                            >
                                                                Review heavy
                                                                images
                                                            </button>
                                                        </div>
                                                    )}
                                                    {hasMissingFavicon && (
                                                        <div
                                                            className={
                                                                hasMissingAltText ||
                                                                hasHeavyImages
                                                                    ? 'mt-2.5 border-t border-neutral-700 pt-2.5'
                                                                    : ''
                                                            }
                                                        >
                                                            <p className="text-[12px] leading-4 text-neutral-200">
                                                                A custom favicon
                                                                has not been
                                                                uploaded.
                                                            </p>
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    close();
                                                                    editor.site.open();
                                                                }}
                                                                className="mt-1.5 inline-flex items-center rounded bg-amber-400 px-2 py-1 text-[10px] font-semibold leading-none text-neutral-900 shadow-sm transition-colors hover:bg-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-300 focus:ring-offset-1 focus:ring-offset-neutral-800"
                                                            >
                                                                Upload favicon
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            <div className="publish-panel-content mt-3 flex items-center justify-between border-t border-neutral-700 pt-2">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        close();
                                                        setUnpublishOpen(true);
                                                    }}
                                                    disabled={
                                                        loading ||
                                                        unpublishing ||
                                                        !publicationStatusKnown ||
                                                        !canUnpublish ||
                                                        !canUnpublishSelected
                                                    }
                                                    className="rounded-md bg-red-600 px-2.5 py-1.5 text-[12px] font-semibold text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40"
                                                >
                                                    Unpublish
                                                </button>
                                                <button
                                                    onClick={handlePublish}
                                                    disabled={
                                                        loading ||
                                                        (!publishDestinations.production &&
                                                            !publishDestinations.staging)
                                                    }
                                                    className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[13px] font-semibold disabled:opacity-50 ${
                                                        hasPublishWarnings
                                                            ? 'bg-amber-400 text-neutral-900 hover:bg-amber-500'
                                                            : 'bg-[#8F52F1] text-white hover:bg-[#7C3AED]'
                                                    }`}
                                                >
                                                    {loading ? (
                                                        <svg
                                                            className="h-3.5 w-3.5 animate-spin"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            aria-hidden="true"
                                                        >
                                                            <circle
                                                                className="opacity-25"
                                                                cx="12"
                                                                cy="12"
                                                                r="10"
                                                                stroke="currentColor"
                                                                strokeWidth="4"
                                                            />
                                                            <path
                                                                className="opacity-75"
                                                                fill="currentColor"
                                                                d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
                                                            />
                                                        </svg>
                                                    ) : (
                                                        <PublishRocketIcon
                                                            className={`h-3.5 w-3.5 ${
                                                                hasPublishWarnings
                                                                    ? 'text-amber-50 drop-shadow-sm'
                                                                    : ''
                                                            }`}
                                                        />
                                                    )}
                                                    {loading
                                                        ? 'Publishing…'
                                                        : 'Publish'}
                                                </button>
                                            </div>
                                        </Popover.Panel>
                                    </Transition>,
                                    portalTarget
                                )}
                        </>
                    );
                }}
            </Popover>
            <ConfirmationModal
                title="Replace the Webflow site?"
                variant="danger"
                message={
                    <div className="space-y-2 text-sm text-gray-500">
                        <p>
                            This site is currently published on Webflow. Are you
                            sure you want to publish this version instead?
                        </p>
                        <p>
                            Publishing will update the live domain to point to
                            this version of the site.
                        </p>
                    </div>
                }
                prompt="Publish this version"
                open={webflowPublishConfirmOpen}
                onClose={() => {
                    if (!loading) setWebflowPublishConfirmOpen(false);
                }}
                onConfirm={() => {
                    setWebflowPublishConfirmOpen(false);
                    performPublish({ confirmWebflowReplacement: true });
                }}
                loading={loading}
            />
            <ConfirmationModal
                title={
                    selectedPublishedDestinations.length > 1
                        ? 'Unpublish selected sites?'
                        : selectedPublishedDestinations[0] === 'staging'
                        ? 'Unpublish preview site?'
                        : 'Unpublish live site?'
                }
                variant="danger"
                message={
                    <div className="space-y-2 text-sm text-gray-500">
                        {selectedPublishedDestinations.includes(
                            'production'
                        ) && (
                            <p>
                                This will detach{' '}
                                <strong className="font-semibold text-gray-700">
                                    {apexLiveDomain}
                                </strong>{' '}
                                and{' '}
                                <strong className="font-semibold text-gray-700">
                                    www.{apexLiveDomain}
                                </strong>{' '}
                                from this Vercel project.
                            </p>
                        )}
                        {selectedPublishedDestinations.includes('staging') && (
                            <p>
                                This will detach{' '}
                                <strong className="font-semibold text-gray-700">
                                    {previewDomain}
                                </strong>{' '}
                                from Vercel and remove its Cloudflare DNS
                                record.
                            </p>
                        )}
                        <p>
                            The project, deployments, domain account, and
                            Publishing again will reconnect the selected
                            domains.
                        </p>
                    </div>
                }
                prompt="Unpublish"
                open={unpublishOpen}
                onClose={() => {
                    if (!unpublishing) setUnpublishOpen(false);
                }}
                onConfirm={handleUnpublish}
                loading={unpublishing}
            />
            <AltTextEditor
                images={usedImages}
                imageSizesByUrl={imageSizesByUrl}
                sources={effectiveAltTextSources}
                focusMode={altTextPanelFocus}
                open={altTextPanelOpen}
                onClose={() => setAltTextPanelOpen(false)}
                onSourcesSaved={setAltTextSources}
                onImageSizeChange={(oldUrl, newUrl, sizeBytes) => {
                    setImageSizesByUrl((current) => {
                        const next = { ...current };
                        delete next[oldUrl];
                        next[newUrl] = sizeBytes;
                        return next;
                    });
                }}
            />
        </>
    );
}
